// 科学的介護推進に関する評価一覧
import React, { FC, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@material-ui/core';
import GeneralPrimaryGhostButton from '../Atoms/GeneralPrimaryGhostButton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import * as UserListField from '../../constants/UserListField';

const useStyles = makeStyles((theme) => ({
  table: {
    display: 'table',
    width: '100%',
    margin: '0 0 80px 0',
    overflow: 'hidden',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    borderRadius: '10px',
    boxShadow: '0 2px 3px 1px #ccc',
    border: '1px',
  },
  tableHeader: {
    padding: '16px 18px',
    fontSize: '14px',
    color: '#fff',
    backgroundColor: '#7c644e',
    textAlign: 'left',
    overflowWrap: 'break-word',
  },
  tableRow: {
    [theme.breakpoints.up('sm')]: {
      '& th, & td': {
        maxWidth: '150px',
        padding: '20px 20px',
        textAlign: 'left',
        fontWeight: 'normal',
        lineHeight: '1.3em',
        overflowWrap: 'break-word',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& th, & td': {
        padding: '20px 7px',
        textAlign: 'left',
        fontWeight: 'normal',
        fontSize: '0.8em',
        overflowWrap: 'break-word',
        verticalAlign: 'middle',
        lineHeight: '1.3em',
      },
      '& th': {
        maxWidth: '105px',
      },
      '& td': {
        maxWidth: '15px',
        '&:first-of-type': {
          maxWidth: '70px',
        },
        '&:nth-of-type(2)': {
          maxWidth: '20px',
          textAlign: 'center',
        },
        '&:nth-of-type(3)': {
          maxWidth: '10px',
          textAlign: 'center',
        },
      },
    },
  },
  report: {
    paddingBottom: '50px',
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 1px 2px 1px rgba(0, 0, 0, 0.3)',
  },
  hideable: {
    padding: '25px 30px 25px 20px',
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#7c644e',
    borderRadius: '10px 10px 0 0',
  },
}));

// 日付をフォーマットする関数
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

// 評価日を表示する関数
const formatEvaluateDate = (year: string, month: string, day: string) => {
  if (!year || !month || !day) return '';
  return `${year}/${month}/${day}`;
};

// 評価日を比較するための日付オブジェクトを作成する関数
const createDateFromEvaluation = (evaluation: any) => {
  if (!evaluation.evaluate_date_year || !evaluation.evaluate_date_month || !evaluation.evaluate_date_day) {
    return new Date(0); // 無効な日付の場合は最も古い日付を返す
  }
  return new Date(
    parseInt(evaluation.evaluate_date_year),
    parseInt(evaluation.evaluate_date_month) - 1, // JavaScriptの月は0から始まる
    parseInt(evaluation.evaluate_date_day),
  );
};

// ステータスを日本語に変換する関数
const translateStatus = (status: string) => {
  switch (status) {
    case '1':
      return 'サービス開始時';
    case '2':
      return 'サービス利用中';
    case '3':
      return 'サービス利用終了時';
    default:
      return '';
  }
};

interface ScientificNursingCarePromotionListProps {
  userId: string;
  user: UserListField.User;
  evaluations: any[];
  onDelete: (userId: string, evaluationId: string) => void;
  onEdit: (userId: string, evaluationId: string) => void;
  onCreateNew: (userId: string) => void;
}

const ScientificNursingCarePromotionList: FC<ScientificNursingCarePromotionListProps> = ({
  userId,
  user,
  evaluations,
  onDelete,
  onEdit,
  onCreateNew,
}) => {
  // 削除確認ダイアログの状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<{ id: string; date: string } | null>(null);
  const classes = useStyles();
  const theme = useTheme();
  const matches = {
    xs: useMediaQuery(theme.breakpoints.down('xs')),
  };
  // 削除確認ダイアログを開く
  const handleDeleteClick = (e: React.MouseEvent, evaluationId: string, date: string) => {
    e.stopPropagation();
    setSelectedEvaluation({ id: evaluationId, date });
    setDeleteDialogOpen(true);
  };

  // 削除を確定
  const handleConfirmDelete = () => {
    if (selectedEvaluation) {
      onDelete(userId, selectedEvaluation.id);
      setDeleteDialogOpen(false);
      setSelectedEvaluation(null);
    }
  };

  // 編集アイコンクリック
  const handleEditClick = (e: React.MouseEvent, evaluationId: string) => {
    e.stopPropagation();
    onEdit(userId, evaluationId);
  };

  return (
    <Box component="article" pb="50px" className={classes.report}>
      <Box className={classes.hideable}>{user.user_name} 様 の【科学的介護推進に関する評価】一覧</Box>
      <Box component="section">
        <Box mx={{ xs: 2, sm: 5 }} my={5}>
          <Grid container spacing={matches.xs ? 2 : 3}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <GeneralPrimaryGhostButton
                  onClick={() => onCreateNew(userId)}
                  innerText={'新しく作成する'}
                  setCss={'width: 120px; height: 40px;'}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              作成した評価を見るには、下記の一覧から、評価日の行を選択してください。
            </Grid>
          </Grid>
        </Box>

        <Box>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.tableHeader} scope="col">
                  評価日
                </th>
                <th className={classes.tableHeader} scope="col">
                  評価時点
                </th>
                <th className={classes.tableHeader} scope="col">
                  作成日
                </th>
                <th className={classes.tableHeader} scope="col">
                  更新日
                </th>
                <th className={classes.tableHeader} scope="col">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {evaluations.length === 0 ? (
                <tr className={classes.tableRow}>
                  <td colSpan={5} style={{ textAlign: 'center' }}>
                    評価データがありません
                  </td>
                </tr>
              ) : (
                // 評価日の降順でソート
                [...evaluations]
                  .sort((a, b) => {
                    const dateA = createDateFromEvaluation(a);
                    const dateB = createDateFromEvaluation(b);
                    return dateB.getTime() - dateA.getTime(); // 降順
                  })
                  .map((evaluation) => {
                    return (
                      <tr
                        style={{ cursor: 'pointer' }}
                        key={evaluation.id}
                        onClick={() => onEdit(userId, evaluation.id)}
                        className={classes.tableRow}
                      >
                        <td>
                          {formatEvaluateDate(
                            evaluation.evaluate_date_year,
                            evaluation.evaluate_date_month,
                            evaluation.evaluate_date_day,
                          )}
                        </td>
                        <td>{translateStatus(evaluation.status)}</td>
                        <td>{formatDate(evaluation.created_at)}</td>
                        <td>{formatDate(evaluation.updated_at)}</td>
                        <td>
                          <Box display="flex">
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, evaluation.id)}
                              aria-label="編集"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleDeleteClick(
                                  e,
                                  evaluation.id,
                                  formatEvaluateDate(
                                    evaluation.evaluate_date_year,
                                    evaluation.evaluate_date_month,
                                    evaluation.evaluate_date_day,
                                  ),
                                )
                              }
                              aria-label="削除"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </Box>

        {/* 削除確認ダイアログ */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">評価データの削除</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {selectedEvaluation && `${selectedEvaluation.date}の評価データを削除しますか？`}
              <br />
              削除したデータは復元できません。
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              キャンセル
            </Button>
            <Button onClick={handleConfirmDelete} color="primary" autoFocus>
              削除する
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ScientificNursingCarePromotionList;
